<template>
  <div id="profile" class="px-40 py-10">
    <div class="mb-6">
      <BlTextTitle4 text="Profile" />
      <BlTextBody2
        text="Personalize your account by updating your profile information."
      />
    </div>
    <div class="mb-6 flex justify-center items-center flex-col">
      <Avatar :name="initial" :size="150" square class="bg-error" status />
      <BlInputButton
        label="Upload Photo"
        type="plain"
        style="width: 150px"
        class="hidden"
      />
    </div>
    <div class="mb-6">
      <BlInputText :input="nameInput" @changed="inputNameHandler" />
    </div>
    <div class="mb-6">
      <div
        class="flex items-center justify-between border-b border-grey-200 py-4"
      >
        <div class="flex items-center gap-2">
          <BlTextBody1 text="Role" />
          <BlIcon
            class="h-6 w-6"
            icon="toast-info"
            tooltip="To change your role, please contact your admin."
          />
        </div>
        <div class="flex items-center gap-2">
          <div
            class="py-1 px-3 flex items-center rounded"
            :class="{
              'bg-cornflower-700': currentUserRole === 'admin',
              'bg-lime-700': currentUserRole === 'staff',
              'bg-orange-700': currentUserRole === 'super_admin'
            }"
          >
            <BlTextBody2
              :text="userRoles[currentUserRole]"
              class="text-pure-white"
            />
          </div>
          <div
            v-if="currentUser.is_owner"
            class="py-1 px-3 flex items-center rounded bg-dark-purple-100"
          >
            <BlTextBody2 text="Owner" class="text-pure-white" />
          </div>
        </div>
      </div>
      <div
        class="w-full flex items-center justify-between border-b border-grey-200 py-4"
      >
        <BlTextBody1 text="Email" />
        <button
          class="flex items-center gap-2 flex-shrink-0 outline-none"
          @click.prevent="emailOpened = true"
        >
          <BlTextBody1 :text="currentUser.email" class="text-grey-400" />
          <BlIcon icon="chevron-right" class="h-6 w-6" />
        </button>
      </div>
      <div
        class="w-full flex items-center justify-between border-b border-grey-200 py-4"
      >
        <BlTextBody1 text="Change Password" />
        <button
          class="flex items-center flex-shrink-0 outline-none"
          @click.prevent="changePasswordOpened = true"
        >
          <BlIcon icon="chevron-right" class="h-6 w-6" />
        </button>
      </div>
    </div>

    <BlBackdrop
      v-if="emailOpened"
      :title="!verified ? 'Verify your password' : 'Change your email'"
      :description="
        !verified
          ? 'For security purposes, please verify your password before changing your email address.'
          : 'Please make sure your new email address is active and accessible before updating your email information.'
      "
      :label="!verified ? 'Continue' : 'Update'"
      :disabled="!verified ? !verifyInput.model : !emailInput.model"
      @backdrop-close="closeBackdrop"
      @backdrop-action="!verified ? verifyPassword() : saveEmail()"
    >
      <div class="px-8">
        <BlInputText
          v-if="!verified"
          :input="verifyInput"
          @changed="inputHandler"
        />
        <div v-else>
          <BlInputText
            :input="{
              label: 'Previous email address',
              model: currentUser.email,
              disabled: true,
              readonly: true
            }"
            class="mb-4"
          />
          <BlInputText :input="emailInput" @changed="inputHandler" />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="changePasswordOpened"
      :title="'Change your password'"
      :description="
        'Protect your account with a stronger password. Please enter your current and new password to keep your personal information secure.'
      "
      :label="'Update'"
      :disabled="!verifyInput.model || !passwordInput.model"
      @backdrop-close="closeBackdrop"
      @backdrop-action="savePassword"
    >
      <div class="px-8">
        <BlInputText
          :input="verifyInput"
          @changed="inputHandler"
          class="mb-4"
        />
        <BlInputText
          :input="passwordInput"
          @changed="inputHandler"
          class="mb-4"
        />
        <div class="grid grid-cols-3 mb-4">
          <div class="flex items-center gap-2">
            <BlIcon
              :icon="
                passwordInputChecked && !passwordHasSixChar ? 'close' : 'check'
              "
              :color="'secondary'"
              class="rounded-full w-6 h-6"
              :class="
                passwordHasSixChar
                  ? 'bg-success-400'
                  : passwordInputChecked
                  ? 'bg-error-300 p-1'
                  : 'bg-grey-300'
              "
            />
            <div class="text-xs">6 characters minimum</div>
          </div>
          <div class="flex items-center gap-2">
            <BlIcon
              :icon="
                passwordInputChecked && !passwordHasUppercase
                  ? 'close'
                  : 'check'
              "
              :color="'secondary'"
              class="rounded-full w-6 h-6"
              :class="
                passwordHasUppercase
                  ? 'bg-success-400'
                  : passwordInputChecked
                  ? 'bg-error-300 p-1'
                  : 'bg-grey-300'
              "
            />
            <div class="text-xs">1 uppercase character</div>
          </div>
        </div>
        <div class="grid grid-cols-3">
          <div class="flex items-center gap-2">
            <BlIcon
              :icon="
                passwordInputChecked && !passwordHasLowescase
                  ? 'close'
                  : 'check'
              "
              :color="'secondary'"
              class="rounded-full w-6 h-6"
              :class="
                passwordHasLowescase
                  ? 'bg-success-400'
                  : passwordInputChecked
                  ? 'bg-error-300 p-1'
                  : 'bg-grey-300'
              "
            />
            <div class="text-xs">1 lowercase character</div>
          </div>
          <div class="flex items-center gap-2">
            <BlIcon
              :icon="
                passwordInputChecked && !passwordHasNumber ? 'close' : 'check'
              "
              :color="'secondary'"
              class="rounded-full w-6 h-6"
              :class="
                passwordHasNumber
                  ? 'bg-success-400'
                  : passwordInputChecked
                  ? 'bg-error-300 p-1'
                  : 'bg-grey-300'
              "
            />
            <div class="text-xs">1 number</div>
          </div>
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";

import Avatar from "../Avatar.vue";
import BlBackdrop from "../BlBackdrop.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputText from "../BlInputText.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";

export default {
  name: "TabProfile",
  components: {
    BlTextSubtitle2,
    BlTextTitle4,
    BlTextBody2,
    Avatar,
    BlInputButton,
    BlInputText,
    BlTextBody1,
    BlIcon,
    BlBackdrop
  },
  data() {
    return {
      userRoles: {
        admin: "Admin",
        staff: "Therapist",
        super_admin: "Super admin"
      },
      nameInput: {
        placeholder: "Type your name",
        label: "Name",
        model: "",
        error: ""
      },
      emailOpened: false,
      changePasswordOpened: false,
      verified: false,
      verifyInput: {
        placeholder: "Type your password",
        label: "Current password",
        model: "",
        inputType: "password",
        error: ""
      },
      emailInput: {
        placeholder: "Type your new email address",
        label: "New email address",
        model: "",
        inputType: "email",
        error: ""
      },
      passwordInput: {
        placeholder: "Type your new password",
        label: "New password",
        model: "",
        inputType: "password",
        error: ""
      },
      passwordInputChecked: false
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    initial() {
      return this.nameInput.model || "-";
    },
    currentUserRole() {
      return this.currentUser.role || "";
    },
    passwordHasSixChar() {
      return this.passwordInput.model.length >= 6;
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.passwordInput.model);
    },
    passwordHasLowescase() {
      return /[a-z]/.test(this.passwordInput.model);
    },
    passwordHasNumber() {
      return /\d/.test(this.passwordInput.model);
    },
    verifiedNewPassword() {
      return (
        this.passwordHasSixChar &&
        this.passwordHasUppercase &&
        this.passwordHasLowescase &&
        this.passwordHasNumber
      );
    }
  },
  mounted() {
    this.nameInput.model = this.currentUser.name;
  },
  methods: {
    ...mapActions([
      "verifyCurrentUserPassword",
      "updateCurrentUser",
      "showToast"
    ]),
    inputNameHandler: debounce(function(payload) {
      payload.input.model = payload.value;
      this.updateName(payload.value);
    }, 1000),
    async updateName(val) {
      if (val === this.currentUser.name) return;
      const params = {
        user: { name: val },
        message: "Success! Your name has been updated"
      };
      await this.updateCurrentUser(params);
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
      payload.input.error = "";
      this.passwordInputChecked = false;
    },
    closeBackdrop() {
      this.verified = false;
      this.emailOpened = false;
      this.changePasswordOpened = false;
      this.verifyInput.model = "";
      this.verifyInput.error = "";
      this.emailInput.model = "";
      this.passwordInput.model = "";
      this.passwordInputChecked = false;
    },
    async verifyPassword() {
      const params = { password: this.verifyInput.model };
      const { success, message } = await this.verifyCurrentUserPassword(params);
      if (!success) {
        this.verifyInput.error = message;
        return;
      }
      this.verified = true;
      const title = "Password verified";
      this.showToast({ title, message: "", type: "success" });
    },
    isValidEmail(val) {
      // Regular expression pattern for email validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      // Test the input against the pattern
      return emailPattern.test(val);
    },
    async saveEmail() {
      if (!this.isValidEmail(this.emailInput.model)) {
        const title = "Invalid email";
        this.showToast({ title, message: "", type: "error" });
        return;
      }

      const params = {
        user: { email: this.emailInput.model },
        message: "Success! Your email has been updated"
      };
      const { success } = await this.updateCurrentUser(params);
      if (!success) return;
      this.closeBackdrop();
    },
    async savePassword() {
      const params = { password: this.verifyInput.model };
      this.passwordInputChecked = true;
      const {
        success: successVerify,
        message
      } = await this.verifyCurrentUserPassword(params);
      if (!successVerify) {
        this.verifyInput.error = message;
        return;
      }

      if (this.verifiedNewPassword) {
        const updated = {
          user: { password: this.passwordInput.model },
          message: "Success! Your password has been updated"
        };
        const { success: successUpdate } = await this.updateCurrentUser(
          updated
        );
        if (!successUpdate) return;
        this.closeBackdrop();
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/profile/TabProfile.vue